import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';


const CaseStudySection = ({ title, description, caseStudies }) => {
    const [activeTab, setActiveTab] = useState(0);
    const current = caseStudies[activeTab];

    return (
        <section className="w-full bg-[#FDFDFD] pb-16 lg:pb-24 relative overflow-hidden text-left">
            <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

                <div className="mb-[30px] lg:mb-[50px] max-w-[700px]">
                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600]">
                        {title}
                    </h2>
                    <p className="text-[#49577A] font-graphik text-[14px] lg:text-[16px] font-[500] mt-2 leading-relaxed">
                        {description}
                    </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
                    {caseStudies.map((tab, idx) => {
                        const isActive = activeTab === idx;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(idx)}
                                className={`p-4 sm:p-5 flex items-center justify-between border-b-[3px] transition-all duration-300 cursor-pointer outline-none bg-white border border-[#E2E8F0] ${isActive
                                        ? 'border-b-[#EA6B24] shadow-sm'
                                        : 'border-b-[#01386A]/20 hover:border-b-[#01386A]/60'
                                    }`}
                            >
                                <div className="flex flex-col items-start text-left">
                                    <span className={`text-[11px] font-sora font-[600] uppercase tracking-wider mb-1 ${isActive ? 'text-[#EA6B24]' : 'text-[#49577A]'
                                        }`}>
                                        0{idx + 1}
                                    </span>
                                    <span className={`font-sora font-[600] text-[14px] sm:text-[16px] tracking-tight ${isActive ? 'text-[#01386A]' : 'text-[#49577A]'
                                        }`}>
                                        {tab.logoText}
                                    </span>
                                </div>
                                <ArrowRight
                                    size={16}
                                    className={`transition-all duration-300 ${isActive
                                            ? 'text-[#EA6B24] translate-x-0 opacity-100'
                                            : 'text-gray-300 -translate-x-2 opacity-0'
                                        }`}
                                />
                            </button>
                        );
                    })}
                </div>

                <div className="relative bg-[#F0F0F0] p-6 sm:p-10 lg:p-12 min-h-[460px] flex flex-col justify-between overflow-hidden shadow-sm">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={current.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35, ease: 'easeOut' }}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative z-10"
                        >
                            <div className="lg:col-span-6 flex flex-col gap-5">
                                <h3 className="text-[#01386A] text-[20px] sm:text-[24px] lg:text-[28px] font-[600] font-sora leading-tight">
                                    {current.title}
                                </h3>

                                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] leading-relaxed">
                                    {current.desc}
                                </p>

                                <div className="pt-2 flex flex-wrap gap-2">
                                    {current.badges.map((badgeText, idx) => (
                                        <span
                                            key={idx}
                                            className="px-3 py-1.5 rounded-full bg-white border border-[#01386A]/15 text-[12px] sm:text-[13px] font-graphik font-[500] text-[#01386A]"
                                        >
                                            {badgeText}
                                        </span>
                                    ))}
                                </div>

                                <div className="pt-3">
                                    <a
                                        href={current.link}
                                        className="inline-flex items-center gap-2 text-[#EA6B24] font-sora font-[600] text-[14px] hover:text-[#01386A] transition-colors duration-200 group"
                                    >
                                        <span>View Full Case Study</span>
                                        <ArrowRight size={16} strokeWidth={2.5} className="group-hover:translate-x-1.5 transition-transform duration-200" />
                                    </a>
                                </div>
                            </div>

                            <div className="lg:col-span-6 relative w-full h-[280px] sm:h-[340px] lg:h-[380px] overflow-hidden rounded-sm bg-black/10">
                                <img
                                    src={current.image}
                                    alt={current.title}
                                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="absolute bottom-0 right-0 w-[220px] h-[180px] pointer-events-none opacity-40 lg:opacity-80 z-0">
                        <img
                            src="/assets/career/wrap1.svg"
                            alt=""
                            className="w-full h-full object-contain object-bottom-right"
                        />
                    </div>
                </div>

            </div>
        </section>
    );
};

export default CaseStudySection;